import { currencyFractionDigits, roundMoney } from "../utils/money.mjs";
import { normalizeLegacyClientScopes } from "./clientAccess";
import { normalizeLocalService } from "./localServiceCatalog";

export const statusClass = (status) => `is2-status is2-status--${String(status).toLowerCase().replace(/\s+/g, "-")}`;

export const parseCurrency = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;

  const source = String(value ?? "").trim();
  if (!source) return 0;
  const negative = source.includes("-") || /^\(.*\)$/.test(source);
  let numeric = source.replace(/[^0-9.,]/g, "");
  const comma = numeric.lastIndexOf(",");
  const dot = numeric.lastIndexOf(".");

  if (comma >= 0 && dot >= 0) {
    numeric = comma > dot
      ? numeric.replace(/\./g, "").replace(",", ".")
      : numeric.replace(/,/g, "");
  } else if (comma >= 0) {
    const decimalDigits = numeric.length - comma - 1;
    numeric = decimalDigits > 0 && decimalDigits <= 2
      ? numeric.replace(",", ".")
      : numeric.replace(/,/g, "");
  }

  const parsed = Number(numeric);
  if (!Number.isFinite(parsed)) return 0;
  return negative ? -Math.abs(parsed) : parsed;
};

const currencyFormatterCache = new Map();

const amountCurrency = (amount, currency) => {
  const explicit = String(currency || "").trim().toUpperCase();
  if (/^[A-Z]{3}$/.test(explicit)) return explicit;
  return String(amount ?? "").match(/\b[A-Z]{3}\b/i)?.[0]?.toUpperCase() || "EUR";
};

const currencyFormatter = (currency, fractionDigits) => {
  const key = `${currency}:${fractionDigits}`;
  if (currencyFormatterCache.has(key)) return currencyFormatterCache.get(key);

  let formatter;
  try {
    formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      currencyDisplay: "symbol",
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  } catch {
    formatter = null;
  }
  currencyFormatterCache.set(key, formatter);
  return formatter;
};

export const formatCurrency = (amount, currency) => {
  const code = amountCurrency(amount, currency);
  const value = roundMoney(parseCurrency(amount), code);
  const fractionDigits = currencyFractionDigits(code);
  const formatter = currencyFormatter(code, fractionDigits);
  if (formatter) return formatter.format(value);
  return `${code} ${value.toLocaleString("en-US", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`;
};

export const invoiceCurrency = (invoice) => {
  const explicit = String(invoice?.currency || "").trim().toUpperCase();
  if (explicit) return explicit;
  return String(invoice?.amount || "").match(/\b[A-Z]{3}\b/)?.[0] || "EUR";
};

export const normalizeInvoiceRecord = (invoice) => {
  if (!invoice) return invoice;
  const currency = invoiceCurrency(invoice);
  return {
    ...invoice,
    currency,
    amount: roundMoney(parseCurrency(invoice.amount), currency),
  };
};

export const normalizeWorkspaceMoney = (data) => {
  if (!data) return data;
  const invoices = Array.isArray(data.invoices) ? data.invoices.map(normalizeInvoiceRecord) : data.invoices;
  const services = Array.isArray(data.services) ? data.services.map(normalizeLocalService) : data.services;
  const recognizablePrototypeRecords = Array.isArray(invoices)
    && invoices.length > 0
    && invoices.every((invoice) => /^cx-2026-\d+$/.test(String(invoice.id || "")));
  return normalizeLegacyClientScopes({
    ...data,
    invoices,
    services,
    sampleWorkspace: data.sampleWorkspace ?? recognizablePrototypeRecords,
  });
};

export const currentIsoDate = (now = new Date()) => `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

export const formatDocumentDate = (value, fallback = "") => {
  if (!value) return fallback;
  const parsed = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return fallback || String(value);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(parsed);
};

export const invoiceNumber = (invoice) => invoice?.invoiceNumber || invoice?.id || `${new Date().getFullYear()}/001`;

export const exportFilename = (invoice, extension) => {
  const safeNumber = invoiceNumber(invoice).replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "");
  const prefix = invoice?.documentType === "credit-note" ? "credit-note" : "invoice";
  return `${prefix}-${safeNumber}${invoice?.status === "Draft" ? "-draft-1" : ""}.${extension}`;
};

export const nextInvoiceNumber = (invoices, issuerId, year = new Date().getFullYear()) => {
  const sequence = invoices.reduce((highest, invoice) => {
    if (invoice.issuerId !== issuerId) return highest;
    const match = String(invoice.invoiceNumber || "").match(/^(\d{4})\/(\d+)$/);
    if (!match || Number(match[1]) !== year) return highest;
    return Math.max(highest, Number(match[2]));
  }, 0);
  return `${year}/${String(sequence + 1).padStart(3, "0")}`;
};

export const paymentTermsLabel = (terms) => ({
  "On receipt": "Due when received",
  "Net 7": "Due within 7 days",
  "Net 14": "Due within 14 days",
  "Net 30": "Due within 30 days",
}[terms] || terms);

export const billingLabels = {
  fixed: { quantity: "Projects", rate: "Project fee", helper: "One agreed fee for the complete piece of work" },
  hour: { quantity: "Hours", rate: "Hourly rate", helper: "Hours worked multiplied by the hourly rate" },
  month: { quantity: "Months", rate: "Monthly fee", helper: "Number of months multiplied by the monthly fee" },
  day: { quantity: "Days", rate: "Daily rate", helper: "Days worked multiplied by the daily rate" },
  item: { quantity: "Items", rate: "Unit price", helper: "Quantity multiplied by the unit price" },
};
